import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationContext';

interface NavigationGuardHook {
  showNavigationWarning: boolean;
  guardNavigation: (path: string) => void;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
}

export const useNavigationGuard = (hasUnsavedChanges: boolean): NavigationGuardHook => {
  const navigate = useNavigate();
  const { 
    showNavigationWarning, 
    pendingNavigation,
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();

  const guardNavigation = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(path);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate, setShowNavigationWarning, setPendingNavigation]);

  const confirmNavigation = useCallback(() => {
    setShowNavigationWarning(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [navigate, pendingNavigation, setShowNavigationWarning, setPendingNavigation]);

  const cancelNavigation = useCallback(() => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  }, [setShowNavigationWarning, setPendingNavigation]);

  return {
    showNavigationWarning,
    guardNavigation,
    confirmNavigation,
    cancelNavigation
  };
};